import { Loader2 } from "lucide-react";
import { without } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { useGetMappingImageUri } from "../../../../api/drawingTool";
import {
  useGetIngredientPackaging,
  useGetIngredientWholeList,
  useGetSupplierIngredientDetail,
  usePutIngredientData,
  usePutSupplierData
} from "../../../../api/ingredients";
import {
  useDeleteMappingIngredient,
  useGetMappingIngredientById,
  usePostMappingIngredientData,
  useUpdateMappingIngredientData
} from "../../../../api/mappingIngredient";
import { usePutEditProduct } from "../../../../api/product";
import { useGetCountries, useGetSpecifyIngredient } from "../../../../api/static-data";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { DrawingContext } from "../../../../components/drawing-tool/DrawingTool";
import TailwindBottomButtonView from "../../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Alert, AlertDescription } from "../../../../components/ui/common/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import { FormSearchSelect } from "../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import CommonSplitContainer from "../../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import { DATA_TYPES } from "../../../../config/constants";
import {
  IngredientDataType,
  IngredientSource,
  IngredientType
} from "../../../../config/enums/IngredientType.enum";
import { MappingSourceType, MappingType } from "../../../../config/enums/MappingSourceType.enum";
import { ROLES } from "../../../../config/permission-maps";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import PackagingSection from "../../../lcaIngredients/ingredientRegister/components/PackagingSection";
import TransportationSection from "../../../lcaIngredients/ingredientRegister/components/TransportationSection";
import IngredientInfoTile from "./components/IngredientInfoTile";
import IngredientProduceTiles from "./components/IngredientProduceTiles";

const initialIngredient = {
  id: null,
  title: "",
  suppliers: [],
  productIds: [],
  ingredient_source: IngredientSource
};

const initialSupplier = {
  id: null,
  title: "",
  supplierData: {
    ingredientIds: [],
    productIds: []
  }
};

interface IngredientSelectForm {
  ingredientId: string;
  supplierId: string;
}

export const IngredientBase = ({ modalData }: any) => {
  const {
    nodeItemId,
    chartEdges,
    chartNodes,
    ownerId,
    sourceId,
    lockStatus,
    productData,
    mappingSourceType,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    saveProductData
  } = useContext(DrawingContext);

  const productId = ownerId;
  const { deleteElements, getNode, getEdges } = useReactFlow();
  const [ingredient, setIngredient] = useState(initialIngredient);
  const [supplier, setSupplier] = useState(initialSupplier);
  const [visible, setVisible] = useState<boolean>(false);
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const form = useForm<IngredientSelectForm>({
    defaultValues: { ingredientId: "", supplierId: "" }
  });

  const { data: ingredientDetails, isLoading: isSuplierIngredientDetailsLoading } =
    useGetSupplierIngredientDetail(supplier.id || "", ingredient.id || "");

  const nodeItem = useMemo(() => getNode(nodeItemId || ""), [nodeItemId]);
  const mappingIngredientId = nodeItem?.data?.reference?.mappingIngredientId || "";

  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const { mutate: saveIngredientData } = usePostMappingIngredientData();
  const { mutate: updateMappingIngredient } = useUpdateMappingIngredientData();
  const { data: mappingIngredientData } = useGetMappingIngredientById(mappingIngredientId);
  const { mutate: deleteMappingIngredient, isLoading } = useDeleteMappingIngredient();

  const isDetailsLoading = ingredient.id && supplier.id ? isSuplierIngredientDetailsLoading : false;

  const {
    data: ingredientListByProduct,
    isLoading: isIngredientListByProductLoading,
    isSuccess
  } = useGetIngredientWholeList(false, IngredientSource.ALL);

  const { isLoading: editProductLoading, mutate: editProductData } = usePutEditProduct();

  const { isLoading: updateIngredientDataLoading, mutateAsync: mutatePutIngredient } =
    usePutIngredientData();

  const { mutateAsync: mutatePutSupplier } = usePutSupplierData();

  const { data: specifyIngredients } = useGetSpecifyIngredient();

  const { data: countriesData, isSuccess: isCountriesDataSuccess } = useGetCountries();

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  const dataTypeName = DATA_TYPES.find((t: any) => t.value === ingredientDetails?.data_type)?.text;

  const specifyIngredientName = specifyIngredients?.find(
    (s: any) => s.value === ingredientDetails?.specifyIngredient
  )?.text;

  const countryName = countries?.find(
    (c: any) => parseInt(c.value) === parseInt(ingredientDetails?.countryOrigin)
  )?.text;

  const isSpecific =
    ingredientDetails && ingredientDetails.data_type === IngredientDataType.SUPPLIER;
  const isIngredientDetailsEmpty = !ingredientDetails && ingredient.id && supplier.id;

  const isSupplierDrawingExsits = ingredientDetails?.product_summary || null;

  const { data: signedUrlData } = useGetMappingImageUri(
    mappingSourceType,
    isSupplierDrawingExsits?.imageKey
  );

  const checkRowOrProcess = useMemo(
    () => ({
      processIngredient:
        ingredientDetails?.ingredient_type === IngredientType.PROCESSED &&
        ingredientDetails?.data_type === IngredientDataType.GENERIC,
      produceIngredient:
        ingredientDetails?.ingredient_type === IngredientType.RAW &&
        ingredientDetails?.data_type === IngredientDataType.GENERIC,
      enableIngredient:
        ingredientDetails?.ingredient_type === IngredientType.RAW &&
        ingredientDetails?.data_type === IngredientDataType.GENERIC
    }),
    [ingredientDetails?.ingredient_type, ingredientDetails?.data_type]
  );

  const { processIngredient, produceIngredient, enableIngredient } = checkRowOrProcess;

  let getPackagingData = {
    ingredientId: ingredient?.id,
    supplierId: supplier?.id,
    loadPackageData: enableIngredient
  };

  const {
    data: ingredientPackageData,
    isLoading: loadIngredientPackageData,
    refetch: refetchPackagingData
  } = useGetIngredientPackaging(getPackagingData);

  useEffect(() => {
    if (isSuccess) {
      const ingredient = ingredients?.find(
        (ing: any) => ing?.id === nodeItem?.data?.reference?.ingredientId
      );
      const supplier = ingredient?.suppliers?.find(
        (sup: any) => sup?._id === nodeItem?.data?.reference?.supplierId
      );
      setIngredient(ingredient || initialIngredient);
      setSupplier({
        id: supplier?._id || null,
        title: supplier?.supplier_name || "",
        supplierData: supplier
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeItem, isSuccess]);

  // Keep the dropdown form in sync with the ingredient/supplier state, which remains
  // the source of truth for all save/update/delete logic below.
  useEffect(() => {
    form.reset({
      ingredientId: ingredient.id ? String(ingredient.id) : "",
      supplierId: supplier.id ? String(supplier.id) : ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredient.id, supplier.id]);

  const disableEdit = ingredient.id && nodeItem?.data.reference && supplier.id ? true : false;

  // ingredients array modifying for dropdown
  const ingredients = useMemo(() => {
    return ingredientListByProduct?.map((pi: any) => {
      return {
        id: pi.ingredientData?._id,
        title: pi.ingredientData?.ingredient_name,
        suppliers: pi.ingredientData?.suppliers,
        productIds: pi.ingredientData?.productIds,
        ingredient_source: pi.ingredientData?.ingredient_source
      };
    });
  }, [ingredientListByProduct]);

  // supplier array according to selected ingredient
  const suppliers = useMemo(() => {
    return ingredient?.suppliers?.map((pi: any) => ({
      id: pi._id,
      title: pi.supplier_name,
      supplierData: pi
    }));
  }, [ingredient]);

  const updateProductData = (status: any) => {
    let checkProductIngredientAvailable = productData?.product_ingredients?.find(
      (e: any) => e === ingredient.id
    );
    if (
      (checkProductIngredientAvailable && !status) ||
      (!checkProductIngredientAvailable && status)
    )
      return;
    const withoutIngredient = without(productData?.product_ingredients, ingredient.id);
    let updateProductIngredient = [...productData?.product_ingredients, ingredient.id];
    let productDetails = {
      _id: productData?._id,
      product_ingredients: status ? withoutIngredient : updateProductIngredient
    };
    editProductData(productDetails, {
      onSuccess(data) {
        saveProductData(data);
      }
    });
  };

  const updateIngredient = (status: any) => {
    let checkProductIngredientAvailable = ingredient?.productIds.some((e: any) => e === productId);

    if (checkProductIngredientAvailable && !status) return;
    const withoutProduct = without(ingredient.productIds, productId);
    let updateProduct = [...ingredient.productIds, productId];
    const updateIngredientData = {
      _id: ingredient.id,
      ingredient_name: ingredient.title,
      productIds: status ? withoutProduct : updateProduct,
      ingredient_source: ingredient.ingredient_source
    };
    mutatePutIngredient(updateIngredientData, {
      onSuccess(data: any) {
        successMessage("Update Ingredient");
      }
    });
  };

  const updateSupplierData = (status: any) => {
    let checkProductIngredientAvailable = supplier?.supplierData?.ingredientIds?.some(
      (e: any) => e === ingredient.id
    );

    let checkProductAvailable = supplier?.supplierData?.productIds?.some(
      (e: any) => e === productId
    );

    if (checkProductIngredientAvailable && checkProductAvailable && !status) return;

    const withoutProduct = without(ingredient.productIds, productId);

    let productIds = status ? withoutProduct : [...supplier.supplierData.productIds, productId];

    let supplierDataUpdate = {
      _id: supplier.id,
      supplier_name: supplier?.title,
      productIds: productIds
    };
    mutatePutSupplier(supplierDataUpdate, {
      onSuccess(data: any) {
        successMessage("Supplier update successfully");
      }
    });
  };

  const deleteIngredient = () => {
    setVisible(true);
  };

  const updateAllData = () => {
    if (mappingSourceType !== MappingSourceType.ingredient) {
      updateProductData(true);
    }
    updateIngredient(true);
    updateSupplierData(true);
  };

  const deleteIngredientNode = () => {
    if (disableEdit) {
      updateAllData();
    }
    deleteElements({ nodes: [{ id: nodeItem?.id || "" }], edges: connectedEdges });
    setNodeItem(null);
    setVisible(false);
    if (mappingIngredientId) {
      deleteMappingIngredient(mappingIngredientId);
    }
  };

  const updateNodeData = async (data: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        label: ingredient.title,
        reference: {
          ingredientId: ingredient.id,
          supplierId: supplier.id,
          mappingIngredientId: data._id
        },
        description: `${ingredient.title} ${supplier.title}`
      }
    };
    const updatedChartNodes = chartNodes.map((n: any) => {
      if (n.id === nodeItem?.id) {
        return updatedNode;
      }
      return n;
    });
    setChartNodes(updatedChartNodes);
    setNodeItem(updatedNode);
    saveDrawing(chartEdges, updatedChartNodes);
  };

  const handleMappingIngredientSave = () => {
    const data = {
      sourceId,
      ownerId,
      ingredientId: ingredient?.id || "",
      supplierId: supplier?.id || "",
      ingredientName: ingredient?.title || "",
      supplierName: supplier?.title || "",
      mappingType: MappingType[mappingSourceType] as unknown as MappingType
    };

    if (mappingIngredientData) {
      updateMappingIngredient(
        { id: mappingIngredientId, data },
        {
          onSuccess: res => {
            updateNodeData(res);
            setNodeItem(null);
          }
        }
      );
    } else {
      saveIngredientData(data, {
        onSuccess: res => {
          updateNodeData(res);
          setNodeItem(null);
        }
      });
    }
  };

  // submit callback function
  const onSubmit = useCallback(() => {
    if (mappingSourceType !== MappingSourceType.ingredient) {
      updateProductData(false);
    }
    handleMappingIngredientSave();
    updateIngredient(false);
    updateSupplierData(false);
  }, [nodeItem, chartEdges, chartNodes, ingredient, supplier, mappingSourceType]);

  // Loading state check
  if (
    isIngredientListByProductLoading ||
    editProductLoading ||
    updateIngredientDataLoading ||
    isDetailsLoading ||
    isLoading
  ) {
    return (
      <div
        className="tw-flex tw-items-center tw-justify-center tw-py-12"
        aria-busy="true"
        aria-live="polite"
      >
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
      </div>
    );
  }

  const handleIngredientChange = (value: string) => {
    const selected = ingredients?.find((ing: any) => String(ing.id) === value);
    setIngredient(selected || initialIngredient);
    setSupplier(initialSupplier);
  };

  const handleSupplierChange = (value: string) => {
    const selected = suppliers?.find((sup: any) => String(sup.id) === value);
    setSupplier(selected || initialSupplier);
  };

  return (
    <>
      <Form {...form}>
        <CommonSplitContainer
          currentHeight={0.93}
          bottomClassName="tw-border-t tw-border-border tw-bg-background"
          topChild={
            <div className="tw-flex tw-flex-col tw-gap-5 tw-pr-1">
              <Card className="tw-rounded-lg tw-border-border tw-gap-3 tw-py-4">
                <CardHeader className="tw-px-4">
                  <CardTitle className="tw-text-sm tw-font-semibold">
                    Ingredient &amp; supplier
                  </CardTitle>
                </CardHeader>
                <CardContent className="tw-flex tw-flex-col tw-gap-4 tw-px-4">
                  <FormSearchSelect
                    control={form.control}
                    name="ingredientId"
                    label="Ingredient Name"
                    required
                    placeholder="Search ingredient..."
                    options={ingredients || []}
                    loading={isIngredientListByProductLoading}
                    onChange={handleIngredientChange}
                  />
                  {ingredient.id ? (
                    <FormSearchSelect
                      control={form.control}
                      name="supplierId"
                      label="Supplier Name"
                      required
                      placeholder="Search supplier..."
                      options={suppliers || []}
                      onChange={handleSupplierChange}
                    />
                  ) : null}
                </CardContent>
              </Card>

              {ingredientDetails ? (
                <Card className="tw-rounded-lg tw-border-border tw-gap-3 tw-py-4">
                  <CardHeader className="tw-px-4">
                    <CardTitle className="tw-text-sm tw-font-semibold">
                      Ingredient information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="tw-flex tw-flex-col tw-gap-4 tw-px-4">
                    {isSupplierDrawingExsits ? (
                      <Alert className="tw-rounded-lg tw-border-primary/30 tw-bg-primary/5">
                        <AlertDescription>
                          Total Climate Change Impact:{" "}
                          <span className="tw-font-semibold tw-text-primary">
                            {checkIsUserSupplier
                              ? "Calculated: Upgrade to see results"
                              : isSupplierDrawingExsits.displayTotalImpactClimateChange}
                          </span>
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                      <IngredientInfoTile
                        label="Ingredient Type"
                        value={ingredientDetails?.ingredient_type}
                      />
                      <IngredientInfoTile label="Data Type" value={dataTypeName} />
                      <IngredientInfoTile label="Unit Size" value={ingredientDetails?.unit_size} />
                      <IngredientInfoTile label="Unit Type" value={ingredientDetails?.unit_type} />
                      <IngredientInfoTile
                        label="EAN Barcode"
                        value={ingredientDetails?.ean_bar_code}
                      />
                    </div>

                    {!isSpecific && produceIngredient ? (
                      <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                        {processIngredient
                          ? "Processed ingredient information"
                          : "Produce Information"}
                      </p>
                    ) : null}

                    {produceIngredient ? (
                      <IngredientProduceTiles
                        produceType={ingredientDetails?.produceType}
                        produceCultivar={ingredientDetails?.produceCultivar}
                        farmingType={ingredientDetails?.farmingType}
                        fieldType={ingredientDetails?.fieldType}
                        countryName={countryName}
                      />
                    ) : null}

                    {processIngredient ? (
                      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                        <IngredientInfoTile label="Specify Ingredient" value={specifyIngredientName} />
                        {!isSpecific ? (
                          <IngredientInfoTile label="Country" value={countryName} />
                        ) : null}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              ) : isIngredientDetailsEmpty ? (
                <Alert variant="destructive" className="tw-rounded-lg">
                  <AlertDescription>
                    No Supplier Ingredient Details Available. Please check if the supplier
                    ingredient details are correctly set.
                  </AlertDescription>
                </Alert>
              ) : null}

              {enableIngredient ? (
                <PackagingSection
                  ingredientId={ingredient?.id || ""}
                  supplierId={supplier?.id || ""}
                  refetchPackagingData={refetchPackagingData}
                  ingredientPackageData={ingredientPackageData}
                  dataView={true}
                />
              ) : null}

              {enableIngredient ? (
                <TransportationSection
                  ingredientId={ingredient?.id || ""}
                  supplierId={supplier?.id || ""}
                  disabled={true}
                />
              ) : null}

              {signedUrlData ? (
                <img
                  src={signedUrlData?.signedUrl}
                  alt="Mapping"
                  className="tw-w-full tw-rounded-lg tw-border tw-border-border"
                />
              ) : null}
            </div>
          }
          bottomChild={
            <TailwindBottomButtonView
              cancelButtonStatus={true}
              cancelButton={() => setNodeItem(null)}
              deleteStatus={true}
              deleteButton={() => deleteIngredient()}
              saveButtonStatus={true}
              saveButton={() => onSubmit()}
              saveTitle={nodeItem?.data.reference && ingredient.id ? "Update" : "Save"}
              cancelDisable={lockStatus}
              deleteDisable={lockStatus}
              saveDisable={lockStatus}
              showDeleteIcon={true}
              customHeight={72}
              customBottomContentMain="!tw-px-2 tw-py-3"
              deleteButtonVariant="ghost"
            />
          }
        />
      </Form>
      {visible &&
        createPortal(
          <ConfirmModal
            viewModal={visible}
            closeModal={() => setVisible(false)}
            cancel={() => {
              setVisible(false);
            }}
            approve={() => {
              deleteIngredientNode();
            }}
            title="Delete Ingredient"
            subTitle="Are you sure you want to remove the ingredient data?"
          />,
          document.body
        )}
    </>
  );
};
